import React from 'react';
import PropTypes from 'prop-types';

/**
 * Stack Component
 * 
 * A layout component for stacking elements vertically or horizontally with consistent spacing.
 * Built with Flexbox and Tailwind utilities for the NuVola design system.
 */

const Stack = ({
  children,
  direction = 'vertical',
  spacing = 4,
  align = 'stretch',
  justify = 'start',
  wrap = false,
  className = '',
  ...props
}) => {
  // Base flex styles
  const baseStyles = ['flex'];

  // Direction configuration
  const directionStyles = {
    vertical: 'flex-col',
    horizontal: 'flex-row'
  };

  // Spacing configuration
  const spacingStyles = {
    vertical: `space-y-${spacing}`,
    horizontal: `space-x-${spacing}`
  };

  // Alignment configuration
  const alignStyles = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
    baseline: 'items-baseline'
  };

  // Justify configuration
  const justifyStyles = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly'
  };

  // Wrap configuration
  const wrapStyles = wrap ? 'flex-wrap' : 'flex-nowrap';

  // Combine all classes
  const stackClasses = [
    ...baseStyles,
    directionStyles[direction],
    spacingStyles[direction],
    alignStyles[align],
    justifyStyles[justify],
    wrapStyles,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={stackClasses} {...props}>
      {children}
    </div>
  );
};

Stack.propTypes = {
  /** Stack content */
  children: PropTypes.node.isRequired,
  
  /** Stack direction */
  direction: PropTypes.oneOf(['vertical', 'horizontal']),
  
  /** Spacing between items */
  spacing: PropTypes.number,
  
  /** Cross-axis alignment */
  align: PropTypes.oneOf(['start', 'center', 'end', 'stretch', 'baseline']),
  
  /** Main-axis justification */
  justify: PropTypes.oneOf(['start', 'center', 'end', 'between', 'around', 'evenly']),
  
  /** Whether items should wrap */
  wrap: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string
};

Stack.defaultProps = {
  direction: 'vertical',
  spacing: 4,
  align: 'stretch',
  justify: 'start',
  wrap: false,
  className: ''
};

export default Stack; 